import React from 'react';
import { useLanguage } from '../../../helpers/language-context';
import { renderHTML } from '../../../helpers/render-html';

const MissionIcon = () => (
  <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-inner">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FDA729" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  </div>
);

const VisionIcon = () => (
  <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-inner">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FDA729" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  </div>
);

const IntegrityIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const CustomerIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ExcellenceIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v20" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const UnityIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const SafetyIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7v5c0 5.52 4.48 10 10 10s10-4.48 10-10V7l-10-5z" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const SectionCard = ({ icon, title, content, isList = false }) => {
  return (
    <div className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-10 rounded-[32px] hover:bg-white/10 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/10 flex flex-col items-start text-left overflow-hidden h-full">
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-orange-500/10 blur-[80px] group-hover:bg-orange-500/20 transition-all duration-700"></div>
      {icon}
      <h3 className="text-xl md:text-3xl font-bold mb-6 text-white group-hover:text-orange-400 transition-colors uppercase tracking-tight">
        {title}
      </h3>
      <div
        className={`text-sm md:text-lg text-gray-300 leading-relaxed font-light ${isList ? '[&_ul]:space-y-3 [&_ul]:list-none [&_li]:before:content-["•"] [&_li]:before:text-orange-500 [&_li]:before:mr-3 [&_li]:flex [&_li]:items-start' : ''}`}
        dangerouslySetInnerHTML={renderHTML(content)}
      />
    </div>
  );
};

const ValueItem = ({ icon, label }) => (
  <div className="group flex flex-col items-center justify-center p-8 rounded-3xl bg-white/5 border border-white/10 transition-all duration-500 hover:bg-orange-500 hover:border-orange-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-orange-500/40 text-center min-h-[220px]">
    <div className="text-orange-400 group-hover:text-white transition-colors duration-500 mb-6 transform group-hover:scale-125">
      {icon}
    </div>
    <span className="text-white font-bold text-sm md:text-base uppercase tracking-wider leading-tight">
      {label}
    </span>
  </div>
);

const MissionVission = () => {
  const { translations, language } = useLanguage();

  const values = language === 'en' ? [
    { label: "Integrity", icon: <IntegrityIcon /> },
    { label: "Customer-Centric", icon: <CustomerIcon /> },
    { label: "Operational Excellence", icon: <ExcellenceIcon /> },
    { label: "Unity", icon: <UnityIcon /> },
    { label: "Priority on Safety, Health, and Environment", icon: <SafetyIcon /> }
  ] : [
    { label: "Berintegriti", icon: <IntegrityIcon /> },
    { label: "Mesra Pelanggan", icon: <CustomerIcon /> },
    { label: "Kecemerlangan Operasi", icon: <ExcellenceIcon /> },
    { label: "Bersatu Padu", icon: <UnityIcon /> },
    { label: "Fokus pada Keselamatan, Kesehatan dan Alam Sekitar", icon: <SafetyIcon /> }
  ];

  return (
    <section className="bg-[#28357B] text-white py-24 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/10 blur-[150px] -z-10 rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-orange-600/5 blur-[100px] -z-10 rounded-full"></div>

      <div className="container mx-auto px-6 max-w-7xl">
        <div className="max-w-4xl mb-24 space-y-6 text-left">
          <div className="inline-block px-4 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-sm font-bold uppercase tracking-wider mb-4 animate-fade-in">
            Core Foundations
          </div>
          <h2 className="text-4xl md:text-7xl font-bold leading-tight animate-fade-in">
            {translations.about.ourMissionValuesVision}
          </h2>
          <p className="text-lg md:text-2xl text-gray-300/80 font-light leading-relaxed max-w-2xl animate-fade-in [animation-delay:200ms]">
            {translations.about.ourMissionValuesVisionDesc}
          </p>
        </div>

        {/* Mission & Vision Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-stretch mb-20 animate-fade-in [animation-delay:300ms]">
          <SectionCard
            icon={<MissionIcon />}
            title={translations.about.ourMission}
            content={translations.about.missionText}
            isList={true}
          />
          <SectionCard
            icon={<VisionIcon />}
            title={translations.about.ourVision}
            content={translations.about.visionText}
          />
        </div>

        {/* Values Horizontal Row */}
        <div className="space-y-12 animate-fade-in [animation-delay:500ms]">
          <div className="flex items-center gap-6">
            <h3 className="text-2xl font-bold uppercase tracking-[0.2em] text-orange-500 whitespace-nowrap">
              {translations.about.ourValues}
            </h3>
            <div className="h-[1px] bg-white/10 flex-1"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {values.map((v, i) => (
              <ValueItem key={i} icon={v.icon} label={v.label} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVission;
